import React, { useEffect, useMemo, useState } from "react";
import {
  DropDown,
  Loader,
  Modal,
  Pagination,
  SocialIcon,
  Tabs,
  SocialArticleCard,
} from "@relentless/rmds/v1/relentless.esm.js";
import FilterBar from "../components/FilterBar.jsx";
import { useInstagramPosts, useYouTubePosts } from "../lib/api";
import { REVEAL_ANIMATIONS, useStaggeredReveal } from "../lib/animations";
import styles from "./SocialPage.module.scss";
import heroImage from "../assets/img/Hero.png";
import instagramIcon from "../assets/icons/instagram.svg";
import youtubeIcon from "../assets/icons/youtube.svg";
import facebookIcon from "../assets/icons/facebook.svg";
import tiktokIcon from "../assets/icons/tiktok.svg";

export default function SocialPage() {
  const [selectedTab, setSelectedTab] = useState("all");
  const [sortBy, setSortBy] = useState("Newest");
  const [pageSize, setPageSize] = useState("All");
  const [page, setPage] = useState(1);
  const [activeItem, setActiveItem] = useState(null);

  // Default mixed social content; used as fallback if CMS not available
  const { posts: instagramPosts, loading: instagramLoading } =
    useInstagramPosts();
  const { posts: youTubePosts, loading: youTubeLoading } = useYouTubePosts();

  const formatMetaDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const instagramItems = useMemo(() => {
    const list = Array.isArray(instagramPosts) ? instagramPosts : [];
    return list
      .map((post, index) => {
        const caption = post?.caption || "";
        const title = post?.title || " ";
        const isVideo =
          String(post?.media_type || "").toUpperCase() === "VIDEO";
        const mediaUrl = post?.media_url || "";
        const thumbUrl = post?.thumbnail_url || "";
        const cardImage = isVideo ? thumbUrl || mediaUrl : mediaUrl || thumbUrl;
        return {
          id: `ig-${post?.id || index}`,
          type: "Instagram",
          title,
          body: caption,
          date: formatMetaDate(post?.timestamp),
          sortTime: post?.timestamp ? Date.parse(post.timestamp) : 0,
          image_url: cardImage,
          media_url: mediaUrl,
          thumbnail_url: thumbUrl,
          media_type: post?.media_type || "",
          link: post?.permalink || "",
          icon_src: instagramIcon,
          seed: `ig-${post?.id || index}`,
        };
      })
      .filter((item) => item.image_url);
  }, [instagramPosts]);

  const youTubeItems = useMemo(() => {
    const list = Array.isArray(youTubePosts) ? youTubePosts : [];
    return list
      .map((post, index) => {
        const title = post?.title || "YouTube video";
        const description = post?.description || "";
        const publishedAt = post?.video_published_at || "";
        const thumbUrl = post?.thumbnail_url || "";
        const videoUrl = post?.video_url || "";
        return {
          id: `yt-${post?.id || index}`,
          type: "YouTube",
          title,
          body: description,
          date: formatMetaDate(publishedAt),
          sortTime: publishedAt ? Date.parse(publishedAt) : 0,
          image_url: thumbUrl,
          media_url: videoUrl,
          thumbnail_url: thumbUrl,
          link: videoUrl,
          icon_src: youtubeIcon,
          seed: post?.yt_id || `yt-${post?.id || index}`,
        };
      })
      .filter((item) => item.image_url || item.media_url);
  }, [youTubePosts]);

  const items = useMemo(() => {
    return [...instagramItems, ...youTubeItems]
      .filter((item) =>
        ["Facebook", "Instagram", "YouTube"].includes(item.type),
      )
      .map((item) => ({
        ...item,
        sortTime:
          typeof item.sortTime === "number" && item.sortTime
            ? item.sortTime
            : Date.parse(item.timestamp || item.date || "") || 0,
      }));
  }, [instagramItems, youTubeItems]);

  const facebookItems = useMemo(
    () => items.filter((item) => item.type === "Facebook"),
    [items],
  );

  const tabItems = useMemo(() => {
    const nextTabs = [{ id: "all", label: "All", hasContent: true }];

    if (facebookItems.length > 0) {
      nextTabs.push({
        id: "Facebook",
        label: "Facebook",
        icon: <SocialIcon name="facebook" src={facebookIcon} size="sm" />,
      });
    }

    if (instagramItems.length > 0) {
      nextTabs.push({
        id: "Instagram",
        label: "Instagram",
        icon: <SocialIcon name="instagram" src={instagramIcon} size="sm" />,
      });
    }

    if (youTubeItems.length > 0) {
      nextTabs.push({
        id: "YouTube",
        label: "YouTube",
        icon: <SocialIcon name="youtube" src={youtubeIcon} size="sm" />,
      });
    }

    return nextTabs;
  }, [facebookItems.length, instagramItems.length, youTubeItems.length]);

  const visible = useMemo(() => {
    if (selectedTab === "all") return items;
    return items.filter((it) => it.type === selectedTab);
  }, [items, selectedTab]);

  const sortedItems = useMemo(() => {
    const list = [...visible];
    if (sortBy === "Platform") {
      return list.sort((a, b) => String(a.type).localeCompare(String(b.type)));
    }
    if (sortBy === "Oldest") {
      return list.sort((a, b) => a.sortTime - b.sortTime);
    }
    return list.sort((a, b) => b.sortTime - a.sortTime);
  }, [visible, sortBy]);

  const totalPages = useMemo(() => {
    if (pageSize === "All") return 1;
    const size = Number(pageSize) || 1;
    return Math.max(1, Math.ceil(sortedItems.length / size));
  }, [pageSize, sortedItems.length]);

  const pagedItems = useMemo(() => {
    if (pageSize === "All") return sortedItems;
    const size = Number(pageSize) || 1;
    const start = (page - 1) * size;
    return sortedItems.slice(start, start + size);
  }, [page, pageSize, sortedItems]);

  const isVideoUrl = (url) => {
    if (!url) return false;
    return /\.(mp4|mov|webm)(\?|#|$)/i.test(String(url));
  };

  const getVideoMimeType = (url) => {
    const normalized = String(url || "").toLowerCase();
    if (/\.webm(\?|#|$)/.test(normalized)) return "video/webm";
    if (/\.mov(\?|#|$)/.test(normalized)) return "video/quicktime";
    if (/\.mp4(\?|#|$)/.test(normalized)) return "video/mp4";
    return undefined;
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return "";
    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, "");
      if (host === "youtu.be") {
        const id = parsed.pathname.replace(/^\//, "");
        return id ? `https://www.youtube.com/embed/${id}` : "";
      }
      if (host.endsWith("youtube.com")) {
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : "";
      }
      return "";
    } catch (_) {
      return "";
    }
  };

  const getInstagramEmbedUrl = (url) => {
    if (!url) return "";
    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/^www\./, "");
      if (!host.endsWith("instagram.com")) return "";
      const pathname = parsed.pathname.replace(/\/+$/, "");
      return pathname ? `https://www.instagram.com${pathname}/embed` : "";
    } catch (_) {
      return "";
    }
  };

  const openPreview = (item) => {
    setActiveItem(item);
  };

  const closePreview = () => setActiveItem(null);

  useEffect(() => {
    setPage(1);
  }, [selectedTab, sortBy, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (tabItems.some((tab) => tab.id === selectedTab)) return;
    setSelectedTab("all");
  }, [selectedTab, tabItems]);

  useStaggeredReveal(styles.rowOverlap, [pagedItems]);

  if (instagramLoading || youTubeLoading) {
    return (
      <div className="rm-page-loading">
        <Loader />
      </div>
    );
  }
  return (
    <div className={styles.page}>
      <section
        className={styles.hero}
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className={styles.heroScrim} aria-hidden="true" />
        <div className={styles.heroInner}>
          <p className={styles.heroKicker}>Social</p>
          <h1 className={styles.heroTitle}>All Social Media</h1>
          <p className={styles.heroBody}>
            Shorts, images, and articles across platforms
          </p>
          <div className={styles.platformRow}>
            {[
              { name: "Instagram", src: instagramIcon },
              { name: "YouTube", src: youtubeIcon },
              { name: "Facebook", src: facebookIcon },
              { name: "TikTok", src: tiktokIcon },
            ].map(({ name, src }) => (
              <div key={name} className={styles.platformBadge}>
                <img src={src} alt={name} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className={styles.main}>
        <div className={styles.tabsRow}>
          <Tabs
            items={tabItems}
            activeId={selectedTab}
            onChange={setSelectedTab}
          />
        </div>
        <div className={styles.content}>
          <FilterBar className={styles.filters}>
            <DropDown
              label="Sort by"
              labelPosition="left"
              displayMode="icon"
              iconOnly={true}
              value={sortBy}
              className={styles.sortMenu}
              options={[
                { label: "Newest", value: "Newest", iconLeft: "event" },
                { label: "Oldest", value: "Oldest", iconLeft: "schedule" },
                {
                  label: "Platform",
                  value: "Platform",
                  iconLeft: "local_offer",
                },
              ]}
              onSelect={(val) => setSortBy(val)}
              onChange={(e) => setSortBy(e.target.value)}
            />

            <DropDown
              label="Page size"
              labelPosition="left"
              displayMode="icon"
              iconOnly={true}
              value={pageSize}
              className={styles.sizeMenu}
              options={["All", "5", "10", "20", "50"].map((label) => ({
                label,
                value: label,
                iconLeft: "view_list",
              }))}
              onSelect={(val) => setPageSize(val)}
              onChange={(e) => setPageSize(e.target.value)}
            />
          </FilterBar>
          <div className={styles.rowOverlap}>
            {instagramLoading && !pagedItems.length
              ? Array.from({ length: 3 }, (_, i) => (
                  <Loader key={`social-loader-${i}`} />
                ))
              : pagedItems.map((it, index) => {
                  return (
                    <div
                      key={`${it.type}-${it.id}`}
                      className={styles.socialCard}
                      data-animation={REVEAL_ANIMATIONS.slideInUp}
                      data-reveal-index={index}
                    >
                      <SocialArticleCard
                        item={it}
                        isVideoUrl={isVideoUrl}
                        onClick={() => openPreview(it)}
                      />
                    </div>
                  );
                })}
          </div>
          {totalPages > 1 && (
            <div className={styles.bodyFooter}>
              <Pagination
                total={totalPages}
                current={page}
                onChange={(next) => setPage(next)}
                pageCount={5}
                showPageSize
                showStatus
                pageSizeOptions={["All", "5", "10", "20", "50"]}
                pageSize={pageSize}
                onPageSizeChange={(val) => setPageSize(val)}
              />
            </div>
          )}
        </div>
      </main>

      <Modal
        open={Boolean(activeItem)}
        onClose={closePreview}
        title={activeItem?.title || "Social media"}
      >
        {activeItem ? (
          <div className={styles.mediaModal}>
            {String(activeItem.type || "").toLowerCase() === "youtube" ? (
              <div className={styles.youtubeEmbed}>
                <iframe
                  className={styles.youtubeFrame}
                  title={activeItem.title || "YouTube video"}
                  src={getYouTubeEmbedUrl(activeItem.media_url)}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : String(activeItem.type || "").toLowerCase() === "instagram" &&
              activeItem.media_type === "VIDEO" &&
              getInstagramEmbedUrl(activeItem.link) ? (
              <div className={styles.youtubeEmbed}>
                <iframe
                  className={styles.youtubeFrame}
                  title={activeItem.title || "Instagram video"}
                  src={getInstagramEmbedUrl(activeItem.link)}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : activeItem.media_type === "VIDEO" ||
              isVideoUrl(activeItem.media_url) ? (
              activeItem.media_url ? (
                <video
                  key={activeItem.media_url}
                  className={styles.mediaFrame}
                  controls
                  playsInline
                  preload="metadata"
                  poster={
                    activeItem.thumbnail_url || activeItem.image_url || ""
                  }
                >
                  <source
                    src={activeItem.media_url}
                    type={getVideoMimeType(activeItem.media_url)}
                  />
                </video>
              ) : (
                <p className={styles.mediaCaption}>
                  Video unavailable for this item.
                </p>
              )
            ) : (
              <img
                className={styles.mediaFrame}
                src={activeItem.media_url || activeItem.image_url || ""}
                alt={activeItem.title || "Social media"}
                loading="lazy"
              />
            )}
            {activeItem.body ? (
              <p className={styles.mediaCaption}>{activeItem.body}</p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
